import styles from './feature-request-detail-page.module.less';
import { APP_ROUTES } from '@/constants/routes';
import { BackButton, ActionButton } from '@/components/v2/page-header/page-header-buttons';
// The edit action is a Link styled with the shared ActionButton class so the
// round pill look is pixel-identical while the edit-form chunk preloads.
import headerButtonStyles from '@/components/v2/page-header/page-header.module.less';
import { BottomSheet } from '@/components/v2/bottom-sheet';
import { BoneyardSkeleton } from '@/lib/boneyard/boneyard-skeleton';
import { BoneyardListFallback } from '@/lib/boneyard/fallbacks';
import { InlineError } from '@/components/ui/feedback/inline-error';
import { PageHeader } from '@/components/v2/page-header';
import { Pencil, Trash2, ThumbsUp, ThumbsDown, Clock } from 'lucide-react';
import { ReactionOverlay } from '@/components/v2/reaction-overlay/reaction-overlay';
import { featureRequestsApiV2, type FeatureRequestDetailV2 } from '@/lib/api/v2/feature-requests';
import { formatDate } from '@/lib/utils/date';
import { notifications } from '@/lib/notifications';
import { useDelayedFlag } from '@/hooks/ui/useDelayedFlag';
import { useSmartBack } from '@/lib/navigation/smartBack';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { v2 } from '@/lib/i18n/v2';

interface DetailPageProps {
  featureRequestId: string;
}

export function FeatureRequestDetailFixture() {
  // Explicit bars rather than the real markup: reusing the page classes made the
  // captured bones abut (0px between the two 37px title lines) so the skeleton
  // read as one grey slab, and the description's 20px side gutter lived in
  // padding, which boneyard includes in the border box, so its bone spanned the
  // full page width. Bars carry their own gaps and are inset like the real text.
  const bar = (height: number, width: string, marginBottom = 8) => (
    <div
      style={{ width, height, marginBottom, borderRadius: 8, background: 'var(--color-border)' }}
    />
  );
  return (
    <div style={{ padding: '0 20px' }}>
      {bar(30, '100%', 6)}
      {bar(30, '100%', 6)}
      {bar(26, '85%', 18)}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {bar(15, '25%', 0)}
        {bar(15, '38%', 0)}
      </div>
      {bar(96, '100%', 0)}
    </div>
  );
}

export function FeatureRequestDetailSkeleton({ loading = true }: { loading?: boolean }) {
  const fixture = import.meta.env.DEV ? <FeatureRequestDetailFixture /> : undefined;

  return (
    <BoneyardSkeleton
      name="feature-request-detail"
      loading={loading}
      fixture={fixture}
      fallback={<BoneyardListFallback count={3} height={48} />}
    >
      {fixture}
    </BoneyardSkeleton>
  );
}

export const FeatureRequestDetailPageV2 = ({ featureRequestId }: DetailPageProps) => {
  const queryClient = useQueryClient();

  // Back + the post-delete landing POP history (return to wherever the user
  // came from) instead of pushing the list, which made browser-back ping-pong
  // through the just-deleted request. Cold opens replace to the list.
  const handleBack = useSmartBack({ to: APP_ROUTES.FEATURE_REQUESTS });

  const { data, isLoading: loading } = useQuery({
    queryKey: ['feature-request', featureRequestId],
    queryFn: async () => {
      const result = await featureRequestsApiV2.getFeatureRequestById(featureRequestId);
      if (!result.ok || !result.value) {
        throw new Error('Failed to load feature request');
      }
      return result.value;
    },
  });
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [voteReaction, setVoteReaction] = useState<'like' | 'unlike' | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await featureRequestsApiV2.deleteFeatureRequest(featureRequestId);
      if (!result.ok) {
        setDeleteError(v2.featureRequestDetail.deleteFailed);
        return;
      }
      // Invalidate the list so the deleted row disappears immediately instead of
      // lingering for the 5m staleTime window after the user navigates back.
      queryClient.invalidateQueries({ queryKey: ['feature-requests'] });
      notifications.success(v2.featureRequestDetail.deleted);
      handleBack();
      setShowDeleteConfirm(false);
    } catch {
      setDeleteError(v2.featureRequestDetail.deleteFailed);
    } finally {
      setDeleting(false);
    }
  };

  const handleVote = async (vote: 'like' | 'unlike') => {
    if (!data || voting) return;
    setVoteError(null);
    setVoting(true);
    try {
      // Map frontend vote types to backend API vote types
      const currentVote =
        data.userVote === 'liked' ? 'like' : data.userVote === 'disliked' ? 'unlike' : null;
      if (currentVote !== vote && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setVoteReaction(vote);
      }
      const newVote = currentVote === vote ? 'none' : vote;
      const result = await featureRequestsApiV2.voteFeatureRequest(featureRequestId, {
        vote: newVote,
      });
      if (result.ok && result.value) {
        queryClient.setQueryData<FeatureRequestDetailV2>(
          ['feature-request', featureRequestId],
          (prev) =>
            prev
              ? {
                  ...prev,
                  likedCount: result.value.likedCount,
                  dislikedCount: result.value.dislikedCount,
                  userVote: result.value.userVote,
                }
              : prev
        );
        // Refresh the list too — otherwise stale counts/votes persist across
        // filters & sorts until the 5m staleTime expires.
        queryClient.invalidateQueries({ queryKey: ['feature-requests'] });
      } else {
        setVoteError(v2.featureRequestDetail.voteFailed);
      }
    } catch {
      setVoteError(v2.featureRequestDetail.voteFailed);
    } finally {
      setVoting(false);
    }
  };

  const detailSkel = useDelayedFlag(loading);

  if (!data && !detailSkel) {
    return <div className={styles.error}>{v2.featureRequestDetail.notFound}</div>;
  }

  return (
    <div className={`${styles.page} page-enter`}>
      {/* Header */}
      <PageHeader
        leftAction={<BackButton onClick={handleBack} />}
        rightAction={
          !data ? undefined : data.isOwner ? (
            <>
              {/* Real <Link> (not button+navigate) so the edit-form chunk warms
                  while the request is read. No disabled-while-deleting state:
                  the delete sheet's overlay covers the header for the whole
                  mutation, so the affordance is unreachable then anyway. */}
              <Link
                to={APP_ROUTES.FEATURE_REQUESTS_EDIT}
                params={{ featureRequestId }}
                preload="viewport"
                preloadDelay={150}
                className={headerButtonStyles.actionButton}
                aria-label="Edit"
              >
                <Pencil size={20} />
              </Link>
              <ActionButton
                icon={Trash2}
                onClick={() => {
                  setDeleteError(null);
                  setShowDeleteConfirm(true);
                }}
                disabled={deleting}
                aria-label="Delete"
              />
            </>
          ) : (
            <>
              <ActionButton
                icon={ThumbsUp}
                onClick={() => handleVote('like')}
                disabled={voting}
                active={data.userVote === 'liked' && voteReaction !== 'like'}
                reaction={
                  voteReaction === 'like' ? (
                    <ReactionOverlay
                      type="up"
                      color="#3ac0a0"
                      onComplete={() => setVoteReaction(null)}
                    />
                  ) : undefined
                }
                aria-label="Like"
              />
              <ActionButton
                icon={ThumbsDown}
                onClick={() => handleVote('unlike')}
                disabled={voting}
                dislike={data.userVote === 'disliked' && voteReaction !== 'unlike'}
                reaction={
                  voteReaction === 'unlike' ? (
                    <ReactionOverlay
                      type="down"
                      color="#ff616d"
                      onComplete={() => setVoteReaction(null)}
                    />
                  ) : undefined
                }
                aria-label="Dislike"
              />
            </>
          )
        }
      />

      <InlineError message={voteError ?? ''} visible={!!voteError} />

      <BoneyardSkeleton
        name="feature-request-detail"
        loading={detailSkel}
        fixture={import.meta.env.DEV ? <FeatureRequestDetailFixture /> : undefined}
        fallback={<BoneyardListFallback count={3} height={48} />}
      >
        {detailSkel ? (
          import.meta.env.DEV ? (
            <FeatureRequestDetailFixture />
          ) : null
        ) : data ? (
          <>
            {/* Title */}
            <div className={styles.titleSection}>
              <h1 className={styles.titlePrefix}>
                {data.isOwner
                  ? v2.featureRequestDetail.wellYou
                  : v2.featureRequestDetail.wellSomeone}
              </h1>
              <h1 className={styles.titlePrefix}>{v2.featureRequestDetail.wishedFor}</h1>
              <p className={styles.content}>{data.content}</p>
              {data.updatedAt && data.updatedAt !== data.createdAt && (
                <p className={styles.editedAt}>
                  <Clock size={12} />
                  {v2.featureRequestForm.editedAt.replace('{date}', formatDate(data.updatedAt))}
                </p>
              )}
            </div>

            {/* Vote counts */}
            <div className={styles.voteCounts}>
              <span className={styles.likedCount}>
                {data.likedCount} {v2.featureRequestDetail.likedIt}
              </span>
              <span className={styles.dislikedCount}>
                {data.dislikedCount} {v2.featureRequestDetail.didntLikeIt}
              </span>
            </div>

            {/* Description */}
            {data.description && <p className={styles.description}>{data.description}</p>}
          </>
        ) : null}
      </BoneyardSkeleton>

      {/* Delete confirmation */}
      <BottomSheet
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Feature Request"
        description="Are you sure you want to delete this feature request? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Keep"
        confirmVariant="danger"
        onConfirm={handleDelete}
      >
        <InlineError message={deleteError ?? ''} visible={!!deleteError} />
      </BottomSheet>
    </div>
  );
};
